import React from 'react'
import { Link } from 'react-router-dom'
import { wednesdayNightSwing } from '../content/classes'
import {
  JeffBezosTestimonial,
  NewZealandTestimonial,
} from 'app/layout/Testimonial'
import { LoopVideo } from 'app/layout/LoopVideo'
import { WhatIsSwingDancingPanel } from '../components/Home/WhatIsSwingDancingPanel'
import { NightSchedule } from '../components/Class/ClassPageParts'
import classVideo from 'assets/stlswing/starting-swing.mp4'
import classPoster from 'assets/stlswing/starting-swing-poster.jpg'
import trailer from 'assets/getdown/trailer.mp4'
import trailerPoster from 'assets/getdown/trailer-poster.jpg'

const reassurances = [
  {
    title: 'No partner needed',
    text: "We rotate partners throughout the class. It's a great way to meet new people and learn more.",
  },
  {
    title: 'No experience needed',
    text: 'Starting Swing is built for people who have never danced before. Everyone in the room is new.',
  },
  {
    title: "Wear what's comfortable",
    text: 'Anything you can move in. Generally, athletic wear is a good idea.',
  },
]

const path = [
  { when: 'Month 1', name: 'Starting Swing', note: 'Wednesdays at 7:30 PM' },
  {
    when: 'Month 2',
    name: 'Continuing Swing',
    note: 'Same night, same time. A new topic every month.',
  },
  {
    when: 'After that',
    name: 'Advancing Swing',
    note: "Go deeper on last month's topic at 6:30 PM",
  },
]

const section = 'max-w-6xl px-4 mx-auto sm:px-6'

const scrollToStart = () => {
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches
  document
    .getElementById('start-here')
    ?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' })
}

export const HomePage = () => (
  <div>
    {/* Hero: video, grabber and links. A little short of full height so the next section peeks in */}
    <section className='relative overflow-hidden text-white bg-ink h-[92vh] min-h-[32rem]'>
      <LoopVideo
        src={trailer}
        poster={trailerPoster}
        className='absolute inset-0 object-cover w-full h-full'
      />
      {/* Dark only at the bottom where the words are, so the dancers stay visible */}
      <div className='absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/20' />

      <div
        className={`relative flex flex-col justify-end h-full pb-12 md:pb-20 ${section}`}>
        <h1 className='h-display'>STL Swing Dance</h1>
        <p className='mt-4 text-xl font-medium md:text-2xl lg:text-3xl'>
          Learn to dance. Meet new people. Get in shape.
        </p>
        <div className='flex flex-col mt-8 space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4'>
          <button
            onClick={scrollToStart}
            data-testid='hero-my-classes-button'
            className='btn btn-primary'>
            Learn More
          </button>
          <Link
            to='/swing-dance-events'
            data-testid='hero-my-events-button'
            className='btn btn-outline-light'>
            View Events
          </Link>
        </div>
      </div>
    </section>

    {/* What it is */}
    <div id='start-here' className='scroll-mt-16'>
      <WhatIsSwingDancingPanel />
    </div>

    <JeffBezosTestimonial />

    {/* The night: the section that matters most */}
    <section className='bg-white'>
      <div
        className={`py-20 md:py-28 md:grid md:grid-cols-12 md:gap-12 md:items-center ${section}`}>
        <LoopVideo
          src={classVideo}
          poster={classPoster}
          className='object-cover w-full aspect-square rounded-3xl md:col-span-5'
        />
        <div className='mt-10 md:mt-0 md:col-span-7'>
          <h2 className='h2 h2-lg'>A class, then a night out</h2>
          <p className='mt-6 body'>
            We don't want to just teach you how to dance. We want to hang out
            with you. Our insufferable personalities make people avoid us, so we
            bake in the 2 hour social dance with the price of the class.
          </p>
          <div className='mt-8'>
            <NightSchedule
              steps={[
                {
                  time: '7:30 PM',
                  title: 'Starting Swing class',
                  description: 'One hour with our instructors.',
                },
                {
                  time: '8:30 PM',
                  title: wednesdayNightSwing.name,
                  description:
                    'Practice what you just learned and meet people. Stay for none of it, some of it, or all of it.',
                },
              ]}
            />
          </div>
          <Link to='/starting-swing' className='mt-8 btn btn-primary'>
            Sign up for Starting Swing
          </Link>
        </div>
      </div>
    </section>

    {/* Why us */}
    <section className='text-white bg-night-glow'>
      <div
        className={`py-14 md:py-20 md:grid md:grid-cols-12 md:gap-12 md:items-center ${section}`}>
        <p
          className='leading-[0.85] uppercase whitespace-nowrap font-display font-black text-spot text-[112px] md:text-[150px] lg:text-[220px] md:col-span-5'
          aria-hidden='true'>
          Top 5
        </p>
        <div className='mt-4 md:mt-0 md:col-span-7'>
          <h2 className='h2'>In the Top 5 studios in the US to learn dance</h2>
          <p className='mt-5 body !text-muted-dark'>
            No matter how much we focus on having a casual good time, a side
            effect of having internationally celebrated teachers is that
            everyone improves quickly. If you want to try out a new hobby? We
            got you. If you want to win national competitions? We also got you.
          </p>
        </div>
      </div>
    </section>

    {/* Objections */}
    <section className={`py-14 md:py-20 ${section}`}>
      <h2 className='h2'>Never danced? That's the point.</h2>
      <div className='grid gap-4 mt-8 md:grid-cols-3'>
        {reassurances.map((item) => (
          <div key={item.title} className='p-6 bg-white rounded-3xl'>
            <h3 className='h3'>{item.title}</h3>
            <p className='mt-2 text-muted'>{item.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Curriculum preview */}
    <section className='text-white bg-ink'>
      <div className={`py-14 md:py-20 ${section}`}>
        <div className='flex flex-wrap items-baseline justify-between gap-4'>
          <h2 className='h2'>Where it goes from here</h2>
          <Link to='/curriculum' className='link-light'>
            See how we teach
          </Link>
        </div>
        <ol className='grid gap-4 mt-8 md:grid-cols-3'>
          {path.map((stage) => (
            <li
              key={stage.name}
              className='p-6 border border-white/20 rounded-3xl bg-white/5'>
              <p className='kicker !text-spot'>{stage.when}</p>
              <p className='mt-2 text-4xl leading-none uppercase font-display font-black'>
                {stage.name}
              </p>
              <p className='mt-2 text-muted-dark'>{stage.note}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <NewZealandTestimonial />
  </div>
)

export default HomePage
